import { DragDropContext } from 'react-beautiful-dnd'
import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ExclamationCircleFilled } from '@ant-design/icons'

// antd
import { Alert, Button, Checkbox, Dropdown, Modal, message } from 'antd'

// dayjs
import dayjs from 'dayjs'

import {
    buildSprintBoardMatrix,
    createMainTaskEpic,
} from '../developmentSprint.service'
import { mainTaskCreate } from '../../taskRefactored/tasks.slice'
import { isTaskMutationSuccess } from '../../taskRefactored/tasks.service'
import {
    clearMainTaskSprintSummary,
    completeMainTaskSprintThunk,
    deleteMainTaskSprintThunk,
    fetchMainTaskSprintSummaryThunk,
    loadDevelopmentSprintBoard,
    moveMainTaskThunk,
    selectDevelopmentSprintState,
    updateMainTaskEpicPositionThunk,
    updateMainTaskEpicThunk,
    updateMainTaskSprintThunk,
} from '../developmentSprint.slice'

// components
import { showActionNotification } from '../../../components/notification'

import CreateSprintModal from './CreateSprintModal'
import CompleteSprintModal from './developmentSprintView/CompleteSprintModal'
import EpicHeaderRow from './developmentSprintView/EpicHeaderRow'
import SprintSection from './developmentSprintView/SprintSection'
import DevelopmentSprintBoardSkeleton from './developmentSprintView/DevelopmentSprintBoardSkeleton'
import { clearProjectEpicOptionsCache } from '../../taskRefactored/components/epicSelector'

// styles
import styles from './DevelopmentSprintView.module.css'

const reorder = (list, startIndex, endIndex) => {
    const result = [...list]
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
}

const isBacklogSprint = (sprint) =>
    /backlog/i.test(String(sprint?.title || sprint?.raw?.title || ''))

const isIssuesSprint = (sprint) =>
    /^issues$/i.test(String(sprint?.title || sprint?.raw?.title || '').trim())

const resolveSprintCreateTaskType = (sprint, options = {}) =>
    options.type ?? (isIssuesSprint(sprint) ? 'Bug' : 'Task')

/** Body for PUT support/main-task/sprint/:id */
const buildSprintUpdatePayload = (sprint, { isActive, isComplete }) => {
    const raw = sprint?.raw || {}
    const title =
        String(raw.title ?? sprint?.title ?? '').trim() || 'Untitled Sprint'
    const startVal = raw.start ?? raw.startDate
    const endVal = raw.end ?? raw.endDate
    const start =
        startVal != null && startVal !== ''
            ? dayjs(startVal).startOf('day').toISOString()
            : dayjs().startOf('day').toISOString()
    const end =
        endVal != null && endVal !== ''
            ? dayjs(endVal).startOf('day').toISOString()
            : dayjs(start).add(14, 'day').startOf('day').toISOString()
    return {
        sprintId: sprint?.id,
        title,
        start,
        end,
        isActive,
        isComplete,
    }
}

const EPIC_CREATE_DRAFT_ID = '__epic_create__'
const NEW_SPRINT_MOVE_VALUE = '__new_sprint__'

const DevelopmentSprintView = ({
    onTaskClick,
    projectId,
    onRefresh,
    filteredAssignees = [],
}) => {
    const dispatch = useDispatch()
    const {
        status,
        error,
        epicsRaw,
        sprintsRaw,
        sprintSummary,
        sprintSummarySprintId,
        sprintSummaryStatus,
    } = useSelector(selectDevelopmentSprintState)
    const noopSetLocalTaskList = useCallback(() => {}, [])
    const refreshTasks = useCallback(
        (skip = 0) => {
            if (onRefresh) {
                onRefresh(skip)
                return
            }
            if (projectId) {
                dispatch(loadDevelopmentSprintBoard({ projectId }))
            }
        },
        [onRefresh, dispatch, projectId]
    )

    useEffect(() => {
        if (!projectId) return
        // Parent owns filtered loads via onRefresh / toolbar query.
        if (onRefresh) return
        dispatch(loadDevelopmentSprintBoard({ projectId }))
    }, [projectId, dispatch, onRefresh])

    const serverLayout = useMemo(
        () => buildSprintBoardMatrix(epicsRaw, sprintsRaw),
        [epicsRaw, sprintsRaw]
    )

    const [epics, setEpics] = useState(serverLayout.epics)
    const [sprints, setSprints] = useState(serverLayout.sprints)
    const [taskBuckets, setTaskBuckets] = useState(serverLayout.buckets)
    const [createEpicUi, setCreateEpicUi] = useState(null)
    const [createEpicSubmitting, setCreateEpicSubmitting] = useState(false)
    const createEpicInputRef = useRef(null)
    const [sprintModalState, setSprintModalState] = useState({
        open: false,
        mode: 'create',
        sprint: null,
    })
    /** e.g. `start:<sprintId>` | `complete:<sprintId>` while PUT is in flight */
    const [sprintMutationKey, setSprintMutationKey] = useState(null)
    const [epicEdit, setEpicEdit] = useState(null)
    const [epicEditSubmitting, setEpicEditSubmitting] = useState(false)
    const epicTitleEditRef = useRef(null)
    const epicTitleEditContainerRef = useRef(null)
    const [createTaskStates, setCreateTaskStates] = useState({})
    const [visibleCompletedSprintIds, setVisibleCompletedSprintIds] = useState(
        []
    )
    const [completeSprintModalState, setCompleteSprintModalState] = useState({
        open: false,
        sprint: null,
        moveToSprintId: null,
        newSprintRange: null,
    })

    useEffect(() => {
        if (status !== 'succeeded') return
        setEpics(serverLayout.epics)
        setSprints(serverLayout.sprints)
        setTaskBuckets(serverLayout.buckets)
    }, [serverLayout, status])

    const sprintHasAtLeastOneTask = useCallback(
        (sprintId) => {
            const byEpic = taskBuckets?.[sprintId]
            if (!byEpic || typeof byEpic !== 'object') return false
            return Object.values(byEpic).some(
                (tasks) => Array.isArray(tasks) && tasks.length > 0
            )
        },
        [taskBuckets]
    )

    const hasAnyActiveSprint = useMemo(
        () => sprints.some((s) => s.raw?.isActive),
        [sprints]
    )

    const completedSprints = useMemo(
        () => sprints.filter((s) => s?.raw?.isComplete),
        [sprints]
    )

    useEffect(() => {
        setVisibleCompletedSprintIds((prev) => {
            const allowed = new Set(completedSprints?.map((s) => s.id))
            return prev.filter((id) => allowed.has(id))
        })
    }, [completedSprints])

    const orderedSprints = useMemo(() => {
        const selectedCompletedSet = new Set(visibleCompletedSprintIds)
        const selectedCompleted = []
        const nonCompleted = []

        sprints.forEach((s) => {
            if (s?.raw?.isComplete) {
                if (selectedCompletedSet.has(s?.id)) {
                    selectedCompleted.push(s)
                }
                return
            }
            nonCompleted.push(s)
        })

        return [...selectedCompleted, ...nonCompleted]
    }, [sprints, visibleCompletedSprintIds])

    const isTaskDone = useCallback((task) => {
        if (!task) return false
        if (task?.isComplete === true) return true
        const status = String(task?.status || task?.raw?.status || '')
            .trim()
            .toLowerCase()
        return (
            status === 'done' || status === 'completed' || status === 'complete'
        )
    }, [])

    const getSprintTaskSummary = useCallback(
        (sprintId) => {
            const byEpic = taskBuckets?.[sprintId]
            if (!byEpic || typeof byEpic !== 'object') {
                return { doneCount: 0, incompleteCount: 0 }
            }
            let doneCount = 0
            let incompleteCount = 0
            Object.values(byEpic).forEach((tasks) => {
                if (!Array.isArray(tasks)) return
                tasks.forEach((task) => {
                    if (isTaskDone(task)) doneCount += 1
                    else incompleteCount += 1
                })
            })
            return { doneCount, incompleteCount }
        },
        [taskBuckets, isTaskDone]
    )

    const openCompleteSprintModal = useCallback(
        (sprint) => {
            if (!sprint?.id) return
            const currentEnd = sprint?.raw?.end ?? sprint?.raw?.endDate ?? null
            const baseStart = currentEnd
                ? dayjs(currentEnd).add(1, 'day').startOf('day')
                : dayjs().startOf('day')
            setCompleteSprintModalState({
                open: true,
                sprint,
                moveToSprintId: null,
                newSprintRange: [baseStart, baseStart.add(14, 'day')],
            })
            dispatch(
                fetchMainTaskSprintSummaryThunk({
                    sprintId: sprint.id,
                })
            )
        },
        [dispatch]
    )

    const closeCompleteSprintModal = useCallback(() => {
        setCompleteSprintModalState({
            open: false,
            sprint: null,
            moveToSprintId: null,
            newSprintRange: null,
        })
        dispatch(clearMainTaskSprintSummary())
    }, [dispatch])

    const initialCreateTask = useMemo(
        () => ({
            open: false,
            text: '',
            assigneeId: null,
            assigneeData: null,
            loading: false,
            _id: null,
            position: 'bottom',
            start: null,
            end: null,
        }),
        []
    )

    const getCreateTaskKey = useCallback(
        (sprintId, epicId) => `${String(sprintId)}::${String(epicId)}`,
        []
    )

    const openCreateTaskInLane = useCallback(
        (sprintId, epicId) => {
            const key = getCreateTaskKey(sprintId, epicId)
            setCreateTaskStates((prev) => ({
                ...prev,
                [key]: {
                    ...initialCreateTask,
                    open: true,
                    _id: key,
                },
            }))
        },
        [getCreateTaskKey, initialCreateTask]
    )

    const setCreateTaskForLane = useCallback(
        (sprintId, epicId) => (newState) => {
            const key = getCreateTaskKey(sprintId, epicId)
            setCreateTaskStates((prev) => {
                const prevState = prev[key] || initialCreateTask
                const resolved =
                    typeof newState === 'function'
                        ? newState(prevState)
                        : newState
                return {
                    ...prev,
                    [key]: resolved,
                }
            })
        },
        [getCreateTaskKey, initialCreateTask]
    )

    const createTaskFromSprintLane = useCallback(
        async (taskName, sprint, epic, options = {}) => {
            if (!projectId || !taskName || !String(taskName).trim()) return
            if (sprint?.raw?.isComplete) return
            const sprintId = sprint?.id
            const epicId = epic?.id
            if (!sprintId || !epicId || epicId === EPIC_CREATE_DRAFT_ID) return

            const laneKey = getCreateTaskKey(sprintId, epicId)
            const laneState = createTaskStates[laneKey] || initialCreateTask
            const assigneeId =
                options.assigneeId ??
                laneState?.assigneeId ??
                laneState?.assigneeData?._id ??
                null
            const laneTasks = taskBuckets?.[sprintId]?.[epicId] || []
            const fallbackStatus =
                laneTasks[0]?.status || laneTasks[0]?.raw?.status || 'To Do'
            const title = String(taskName).trim()

            const payload = {
                title,
                projectId,
                description: '',
                type: resolveSprintCreateTaskType(sprint, options),
                status: fallbackStatus,
                priority: 'Medium',
                assigneeId,
                start: laneState?.start || null,
                end: laneState?.end || null,
                sprintId,
                epic: epicId,
                epicId,
            }

            try {
                const result = await dispatch(mainTaskCreate(payload))
                const d = result?.payload?.data
                if (!isTaskMutationSuccess(d)) {
                    showActionNotification({
                        type: 'error',
                        message: d?.message || 'Could not create task',
                    })
                    return
                }

                showActionNotification({
                    message: 'Task created successfully',
                    subMessage: title,
                    actionButtonText: 'Undo',
                    notificationKey: 'task-add',
                })

                setCreateTaskStates((prev) => ({
                    ...prev,
                    [laneKey]: initialCreateTask,
                }))
                refreshTasks(0)
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to create task',
                })
            }
        },
        [
            projectId,
            getCreateTaskKey,
            createTaskStates,
            initialCreateTask,
            taskBuckets,
            dispatch,
            refreshTasks,
        ]
    )

    const openCreateSprintModal = useCallback(() => {
        setSprintModalState({
            open: true,
            mode: 'create',
            sprint: null,
        })
    }, [])

    const openEditSprintModal = useCallback((sprint) => {
        if (!sprint?.id) return
        setSprintModalState({
            open: true,
            mode: 'edit',
            sprint,
        })
    }, [])

    const closeSprintModal = useCallback(() => {
        setSprintModalState((prev) => ({ ...prev, open: false, sprint: null }))
    }, [])

    const epicsForBoard = useMemo(() => {
        if (!createEpicUi) return epics
        const draft = {
            id: EPIC_CREATE_DRAFT_ID,
            title: '',
            epicKey: '',
            isDraft: true,
            raw: null,
        }
        const next = [...epics]
        next.splice(createEpicUi.insertIndex, 0, draft)
        return next
    }, [epics, createEpicUi])

    const epicGridStyle = useMemo(
        () => ({
            gridTemplateColumns: `repeat(${Math.max(epicsForBoard.length, 1)}, minmax(200px, 170px))`,
        }),
        [epicsForBoard.length]
    )

    const epicHScrollRef = useRef(null)
    const sprintHScrollRefs = useRef({})
    const syncingHorizontalScroll = useRef(false)

    const syncHorizontalScroll = useCallback((sourceEl) => {
        if (!sourceEl || syncingHorizontalScroll.current) return
        syncingHorizontalScroll.current = true
        const left = sourceEl.scrollLeft
        const epicEl = epicHScrollRef.current
        if (epicEl && epicEl !== sourceEl) {
            epicEl.scrollLeft = left
        }
        Object.values(sprintHScrollRefs.current).forEach((el) => {
            if (el && el !== sourceEl) {
                el.scrollLeft = left
            }
        })
        requestAnimationFrame(() => {
            syncingHorizontalScroll.current = false
        })
    }, [])

    /** 'left' | 'right' — which edge insert UI to show (based on hover x within card). */
    const [epicInsertHover, setEpicInsertHover] = useState(null)

    const handleEpicCardMouseMove = useCallback((e, epicId) => {
        const el = e.currentTarget
        const r = el.getBoundingClientRect()
        const x = e.clientX - r.left
        const side = x < r.width / 2 ? 'left' : 'right'
        setEpicInsertHover((prev) => {
            if (prev?.id === epicId && prev?.side === side) return prev
            return { id: epicId, side }
        })
    }, [])

    const handleEpicCardMouseLeave = useCallback(() => {
        setEpicInsertHover(null)
    }, [])

    const openCreateEpicAt = useCallback((epicIndex, side) => {
        const insertIndex = side === 'left' ? epicIndex : epicIndex + 1
        setEpicInsertHover(null)
        setEpicEdit(null)
        setCreateEpicUi({
            insertIndex,
            title: '',
            description: '',
        })
    }, [])

    const closeCreateEpic = useCallback(() => {
        setCreateEpicUi(null)
    }, [])

    const submitCreateEpic = useCallback(async () => {
        if (!createEpicUi || !projectId) return
        const title = createEpicUi.title.trim()
        if (!title) return
        setCreateEpicSubmitting(true)
        try {
            const res = await createMainTaskEpic({
                projectId,
                title,
                description: (createEpicUi.description || '').trim(),
                position: createEpicUi.insertIndex,
            })
            const body = res?.data ?? res
            if (body?.success === false) {
                showActionNotification({
                    type: 'error',
                    message: body?.message || 'Could not create epic',
                })
                return
            }
            showActionNotification({
                type: 'success',
                message: body?.message || 'Epic created',
                subMessage: title,
            })
            clearProjectEpicOptionsCache(projectId)
            refreshTasks()
            setCreateEpicUi(null)
        } catch (err) {
            showActionNotification({
                type: 'error',
                message:
                    err?.response?.data?.message ||
                    err?.message ||
                    'Could not create epic',
            })
        } finally {
            setCreateEpicSubmitting(false)
        }
    }, [createEpicUi, projectId, dispatch, refreshTasks])

    useEffect(() => {
        if (!createEpicUi) return
        const t = window.setTimeout(() => {
            createEpicInputRef.current?.focus?.()
        }, 0)
        return () => window.clearTimeout(t)
    }, [createEpicUi])

    useEffect(() => {
        if (!epicEdit) return
        const t = window.setTimeout(() => {
            epicTitleEditRef.current?.focus?.()
        }, 0)
        return () => window.clearTimeout(t)
    }, [epicEdit])

    useEffect(() => {
        if (!epicEdit) return
        const onPointerDown = (e) => {
            if (epicEditSubmitting) return
            const root = epicTitleEditContainerRef.current
            if (root?.contains(e.target)) return
            setEpicEdit(null)
        }
        document.addEventListener('mousedown', onPointerDown)
        return () => document.removeEventListener('mousedown', onPointerDown)
    }, [epicEdit, epicEditSubmitting])

    const openEpicTitleEdit = useCallback((epic) => {
        if (!epic?.id || epic.id === EPIC_CREATE_DRAFT_ID) return
        setEpicInsertHover(null)
        setCreateEpicUi(null)
        setEpicEdit({
            epicId: epic.id,
            title: epic.title ?? '',
            description: epic.raw?.description ?? '',
        })
    }, [])

    const submitEpicTitleEdit = useCallback(async () => {
        if (!epicEdit || !projectId || epicEditSubmitting) return
        const title = epicEdit.title.trim()
        if (!title) {
            message.warning('Epic title cannot be empty')
            return
        }
        const description = (epicEdit.description ?? '').trim()
        setEpicEditSubmitting(true)
        try {
            const payload = await dispatch(
                updateMainTaskEpicThunk({
                    epicId: epicEdit.epicId,
                    projectId,
                    title,
                    description,
                })
            ).unwrap()
            if (payload?.success === false) {
                showActionNotification({
                    type: 'error',
                    message: payload?.message || 'Could not update epic',
                })
                return
            }
            showActionNotification({
                type: 'success',
                message: payload?.message || 'Epic updated',
                subMessage: epicEdit.title,
            })
            setEpicEdit(null)
            refreshTasks()
        } catch (err) {
            showActionNotification({
                type: 'error',
                message:
                    err?.response?.data?.message ||
                    err?.message ||
                    'Could not update epic',
            })
        } finally {
            setEpicEditSubmitting(false)
        }
    }, [epicEdit, projectId, epicEditSubmitting, dispatch, refreshTasks])

    const handleStartSprint = useCallback(
        async (sprint) => {
            if (!projectId || !sprint?.id) return
            const key = `start:${sprint?.id}`
            setSprintMutationKey(key)
            try {
                const body = buildSprintUpdatePayload(sprint, {
                    isActive: true,
                    isComplete: false,
                })
                const payload = await dispatch(
                    updateMainTaskSprintThunk(body)
                ).unwrap()
                if (payload?.success === false) {
                    showActionNotification({
                        type: 'error',
                        message: payload?.message || 'Could not start sprint',
                    })
                    return
                }
                showActionNotification({
                    type: 'success',
                    message: payload?.message || 'Sprint started',
                    subMessage: sprint.title,
                })
                refreshTasks()
            } catch (err) {
                const msg =
                    typeof err === 'string'
                        ? err
                        : err?.message || 'Could not start sprint'
                showActionNotification({
                    type: 'error',
                    message: msg,
                })
            } finally {
                setSprintMutationKey(null)
            }
        },
        [projectId, dispatch, refreshTasks]
    )

    const handleCompleteSprint = useCallback(
        async (sprint, completePayload) => {
            if (!projectId || !sprint?.id) return
            const key = `complete:${sprint?.id}`
            setSprintMutationKey(key)
            try {
                const payload = await dispatch(
                    completeMainTaskSprintThunk({
                        sprintId: sprint.id,
                        ...completePayload,
                    })
                ).unwrap()
                if (payload?.success === false) {
                    showActionNotification({
                        type: 'error',
                        message:
                            payload?.message || 'Could not complete sprint',
                    })
                    return
                }
                showActionNotification({
                    type: 'success',
                    message: payload?.message || 'Sprint completed',
                    subMessage: sprint.title,
                })
                refreshTasks()
                return true
            } catch (err) {
                const msg =
                    typeof err === 'string'
                        ? err
                        : err?.message || 'Could not complete sprint'
                showActionNotification({
                    type: 'error',
                    message: msg,
                })
                return false
            } finally {
                setSprintMutationKey(null)
            }
        },
        [projectId, dispatch, refreshTasks]
    )

    const handleDeleteSprint = useCallback(
        async (sprint, moveTasksTo) => {
            if (!projectId || !sprint?.id || !moveTasksTo) return false
            const key = `delete:${sprint?.id}`
            setSprintMutationKey(key)
            try {
                const payload = await dispatch(
                    deleteMainTaskSprintThunk({
                        sprintId: sprint.id,
                        moveTasksTo,
                    })
                ).unwrap()
                if (payload?.success === false) {
                    showActionNotification({
                        type: 'error',
                        message: payload?.message || 'Could not delete sprint',
                    })
                    return false
                }
                showActionNotification({
                    type: 'success',
                    message: payload?.message || 'Sprint deleted',
                    subMessage: sprint.title,
                })
                refreshTasks()
                return true
            } catch (err) {
                const msg =
                    typeof err === 'string'
                        ? err
                        : err?.message || 'Could not delete sprint'
                showActionNotification({
                    type: 'error',
                    message: msg,
                })
                return false
            } finally {
                setSprintMutationKey(null)
            }
        },
        [projectId, dispatch, refreshTasks]
    )

    const requestDeleteSprint = useCallback(
        (sprint) => {
            if (!sprint?.id) return
            const activeNonCompleted = orderedSprints.filter(
                (s) => s?.id && !s?.raw?.isComplete
            )
            const currentIndex = activeNonCompleted.findIndex(
                (s) => String(s.id) === String(sprint.id)
            )
            const backlog = activeNonCompleted.find((s) => isBacklogSprint(s))
            const nextSprint = activeNonCompleted.find(
                (s, idx) =>
                    idx > currentIndex &&
                    String(s.id) !== String(sprint.id) &&
                    !isBacklogSprint(s)
            )
            const moveTasksTo = nextSprint?.id || backlog?.id || 'backlog'

            Modal.confirm({
                title: 'Delete sprint',
                icon: (
                    <ExclamationCircleFilled
                        style={{ color: '#dc2626', fontSize: 18 }}
                    />
                ),
                content: (
                    <span>
                        Are you sure you want to delete sprint{' '}
                        <strong>{sprint.title || ''}</strong>?
                    </span>
                ),
                okText: 'Delete',
                cancelText: 'Cancel',
                okType: 'danger',
                okButtonProps: {
                    size: 'small',
                    type: 'primary',
                    danger: true,
                    className: styles.deleteSprintConfirmBtn,
                },
                cancelButtonProps: {
                    size: 'small',
                    className: styles.deleteSprintCancelBtn,
                },
                centered: true,
                maskClosable: true,
                onOk: () => handleDeleteSprint(sprint, moveTasksTo),
            })
        },
        [orderedSprints, handleDeleteSprint]
    )

    const confirmCompleteSprint = useCallback(async () => {
        const sprint = completeSprintModalState?.sprint
        if (!sprint?.id) return
        const moveTo = completeSprintModalState?.moveToSprintId
        if (!moveTo) {
            message.warning('Please select where to move incomplete tasks')
            return
        }

        let completePayload
        if (moveTo === NEW_SPRINT_MOVE_VALUE) {
            const range = completeSprintModalState?.newSprintRange
            if (!Array.isArray(range) || !range[0] || !range[1]) {
                message.warning('Please select dates for the new sprint')
                return
            }
            const rawCurrentTitle = String(sprint?.title || '').trim()
            const baseTitle =
                rawCurrentTitle.replace(/\s+\d+$/, '').trim() || 'Sprint'
            const normalized = baseTitle.toLowerCase()
            const escapedBase = baseTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            let maxSuffix = 0
            sprints.forEach((s) => {
                const t = String(s?.title || '').trim()
                if (!t) return
                const tNormalized = t.toLowerCase()
                if (tNormalized === normalized) {
                    maxSuffix = Math.max(maxSuffix, 0)
                    return
                }
                const m = t.match(new RegExp(`^${escapedBase}\\s+(\\d+)$`, 'i'))
                if (m?.[1]) {
                    maxSuffix = Math.max(maxSuffix, Number(m[1]) || 0)
                }
            })
            const nextTitle = `${baseTitle} ${Math.max(maxSuffix + 1, 1)}`
            completePayload = {
                moveOpenTasksTo: 'new',
                newSprint: {
                    title: nextTitle,
                    start: dayjs(range[0]).format('YYYY-MM-DD'),
                    end: dayjs(range[1]).format('YYYY-MM-DD'),
                },
            }
        } else if (moveTo === 'backlog') {
            completePayload = { moveOpenTasksTo: 'backlog' }
        } else {
            completePayload = { moveOpenTasksTo: moveTo }
        }

        const ok = await handleCompleteSprint(sprint, completePayload)
        if (ok) closeCompleteSprintModal()
    }, [
        completeSprintModalState?.sprint,
        completeSprintModalState?.moveToSprintId,
        completeSprintModalState?.newSprintRange,
        closeCompleteSprintModal,
        handleCompleteSprint,
        sprints,
    ])

    const onDragEnd = (result) => {
        const { destination, source, type } = result
        if (!destination) return

        if (type === 'epic') {
            if (createEpicUi || epicEdit) return
            if (source.index === destination.index) return
            const movedEpic = epics[source.index]
            setEpics((prev) => reorder(prev, source.index, destination.index))
            if (
                movedEpic?.id &&
                movedEpic.id !== EPIC_CREATE_DRAFT_ID &&
                projectId
            ) {
                dispatch(
                    updateMainTaskEpicPositionThunk({
                        epicId: movedEpic.id,
                        projectId,
                        position: destination.index,
                    })
                )
                    .unwrap()
                    .catch(() => {
                        showActionNotification({
                            type: 'error',
                            message: 'Could not update epic order',
                        })
                        refreshTasks()
                    })
            }
            return
        }

        const [sourceSprintId, sourceEpicId] = source.droppableId.split('::')
        const [destSprintId, destEpicId] = destination.droppableId.split('::')
        if (!sourceSprintId || !sourceEpicId || !destSprintId || !destEpicId)
            return
        if (
            sourceSprintId === destSprintId &&
            sourceEpicId === destEpicId &&
            source.index === destination.index
        )
            return
        if (
            sourceEpicId === EPIC_CREATE_DRAFT_ID ||
            destEpicId === EPIC_CREATE_DRAFT_ID
        )
            return

        const movingTask =
            taskBuckets?.[sourceSprintId]?.[sourceEpicId]?.[source.index]
        if (!movingTask?._id || !projectId) return

        setTaskBuckets((prev) => {
            const next = { ...prev }
            const srcSprint = { ...(next[sourceSprintId] || {}) }
            const dstSprint =
                sourceSprintId === destSprintId
                    ? srcSprint
                    : { ...(next[destSprintId] || {}) }

            const srcList = [...(srcSprint[sourceEpicId] || [])]
            const dstList =
                sourceSprintId === destSprintId && sourceEpicId === destEpicId
                    ? srcList
                    : [...(dstSprint[destEpicId] || [])]

            const [moved] = srcList.splice(source.index, 1)
            if (!moved) return prev
            dstList.splice(destination.index, 0, moved)

            srcSprint[sourceEpicId] = srcList
            dstSprint[destEpicId] = dstList
            next[sourceSprintId] = srcSprint
            next[destSprintId] = dstSprint
            return next
        })

        const destinationSprint = sprints.find(
            (s) => String(s?.id) === String(destSprintId)
        )
        const payload = {
            taskId: String(movingTask._id),
            projectId,
            sprintId: isBacklogSprint(destinationSprint) ? null : destSprintId,
            status:
                movingTask?.status ||
                movingTask?.raw?.status ||
                movingTask?.taskStatus ||
                'To Do',
            initialPosition: source.index,
            position: destination.index,
            epic: destEpicId,
        }

        dispatch(moveMainTaskThunk(payload))
            .unwrap()
            .then((res) => {
                if (res?.success === false) {
                    throw new Error(res?.message || 'Could not move task')
                }
            })
            .catch((err) => {
                showActionNotification({
                    type: 'error',
                    message:
                        (typeof err === 'string' ? err : err?.message) ||
                        'Could not move task',
                })
                refreshTasks()
            })
    }

    const loading = status === 'loading'
    const failed = status === 'failed'

    if (!projectId) {
        return (
            <div className={styles.container}>
                <Alert
                    type="info"
                    showIcon
                    message="Open a project to view sprints."
                />
            </div>
        )
    }

    const modalSprintId = completeSprintModalState?.sprint?.id
    const localCompleteSprintSummary = getSprintTaskSummary(modalSprintId)
    const hasApiSummary =
        sprintSummarySprintId &&
        modalSprintId &&
        String(sprintSummarySprintId) === String(modalSprintId) &&
        sprintSummary
    const completeSprintSummary = hasApiSummary
        ? {
              doneCount: Number(sprintSummary.completedCount ?? 0),
              incompleteCount: Number(sprintSummary.openCount ?? 0),
          }
        : localCompleteSprintSummary
    const completeSprintTargetOptions = hasApiSummary
        ? (sprintSummary.otherSprints || []).map((s) => {
              const label = s?.title || 'Untitled Sprint'
              const id = String(s?._id || '')
              return {
                  label,
                  value: /backlog/i.test(label) ? 'backlog' : id,
              }
          })
        : sprints
              .filter(
                  (s) => s?.id && s?.id !== modalSprintId && !s?.raw?.isComplete
              )
              .map((s) => {
                  const label = s.title || 'Untitled Sprint'
                  return {
                      label,
                      value: isBacklogSprint(s) ? 'backlog' : s.id,
                  }
              })
    const completeModalOptions = [
        ...completeSprintTargetOptions.filter((o) => o.value),
        { label: 'New Sprint', value: NEW_SPRINT_MOVE_VALUE },
    ]
    const isNewSprintSelected =
        completeSprintModalState?.moveToSprintId === NEW_SPRINT_MOVE_VALUE
    const sprintTitleForNew = String(
        completeSprintModalState?.sprint?.title || ''
    )
        .trim()
        .replace(/\s+\d+$/, '')
        .trim()
    const basePreviewTitle = sprintTitleForNew || 'Sprint'
    const escapedPreviewBase = basePreviewTitle.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
    )
    let maxPreviewSuffix = 0
    sprints.forEach((s) => {
        const t = String(s?.title || '').trim()
        if (!t) return
        if (t.toLowerCase() === basePreviewTitle.toLowerCase()) {
            maxPreviewSuffix = Math.max(maxPreviewSuffix, 0)
            return
        }
        const m = t.match(new RegExp(`^${escapedPreviewBase}\\s+(\\d+)$`, 'i'))
        if (m?.[1])
            maxPreviewSuffix = Math.max(maxPreviewSuffix, Number(m[1]) || 0)
    })
    const newSprintTitlePreview = `${basePreviewTitle} ${Math.max(
        maxPreviewSuffix + 1,
        1
    )}`
    const completingCurrentSprint =
        sprintMutationKey === `complete:${completeSprintModalState?.sprint?.id}`

    return (
        <div className={styles.container}>
            {failed && (
                <Alert
                    type="error"
                    showIcon
                    message={
                        typeof error === 'string'
                            ? error
                            : 'Failed to load sprint board'
                    }
                    style={{ margin: '0 12px 12px' }}
                />
            )}
            <DragDropContext onDragEnd={onDragEnd}>
                <div className={styles.scrollShell}>
                    <div className={styles.boardInner}>
                        {loading && !sprints.length ? (
                            <DevelopmentSprintBoardSkeleton />
                        ) : (
                            <>
                                {completedSprints?.length > 0 && (
                                    <div className={styles.sprintFiltersRow}>
                                        <Dropdown
                                            trigger={['click']}
                                            dropdownRender={() => (
                                                <div
                                                    className={
                                                        styles.sprintFilterMenu
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.sprintFilterHead
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.sprintFilterTitle
                                                            }
                                                        >
                                                            Sprint Swimlanes
                                                        </div>
                                                        <div
                                                            className={
                                                                styles.sprintFilterSubtitle
                                                            }
                                                        >
                                                            Show completed
                                                            sprints
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.sprintFilterList
                                                        }
                                                    >
                                                        {completedSprints?.map(
                                                            (sprint) => {
                                                                const checked =
                                                                    visibleCompletedSprintIds.includes(
                                                                        sprint.id
                                                                    )
                                                                return (
                                                                    <label
                                                                        key={
                                                                            sprint.id
                                                                        }
                                                                        className={
                                                                            styles.sprintFilterItem
                                                                        }
                                                                    >
                                                                        <Checkbox
                                                                            checked={
                                                                                checked
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) => {
                                                                                const on =
                                                                                    e
                                                                                        .target
                                                                                        .checked
                                                                                setVisibleCompletedSprintIds(
                                                                                    (
                                                                                        prev
                                                                                    ) =>
                                                                                        on
                                                                                            ? [
                                                                                                  ...prev,
                                                                                                  sprint.id,
                                                                                              ]
                                                                                            : prev.filter(
                                                                                                  (
                                                                                                      id
                                                                                                  ) =>
                                                                                                      id !==
                                                                                                      sprint.id
                                                                                              )
                                                                                )
                                                                            }}
                                                                        />
                                                                        <span>
                                                                            {
                                                                                sprint.title
                                                                            }
                                                                        </span>
                                                                    </label>
                                                                )
                                                            }
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        >
                                            <Button
                                                size="small"
                                                className={
                                                    styles.sprintFilterButton
                                                }
                                            >
                                                Sprint Swimlanes
                                                <Icon
                                                    icon="mdi:chevron-down"
                                                    width={16}
                                                    height={16}
                                                    className={
                                                        styles.sprintFilterChevron
                                                    }
                                                />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                )}

                                <EpicHeaderRow
                                    epicHScrollRef={epicHScrollRef}
                                    syncHorizontalScroll={syncHorizontalScroll}
                                    epicGridStyle={epicGridStyle}
                                    epicsForBoard={epicsForBoard}
                                    EPIC_CREATE_DRAFT_ID={EPIC_CREATE_DRAFT_ID}
                                    createEpicUi={createEpicUi}
                                    epicEdit={epicEdit}
                                    handleEpicCardMouseMove={
                                        handleEpicCardMouseMove
                                    }
                                    handleEpicCardMouseLeave={
                                        handleEpicCardMouseLeave
                                    }
                                    createEpicInputRef={createEpicInputRef}
                                    createEpicSubmitting={createEpicSubmitting}
                                    setCreateEpicUi={setCreateEpicUi}
                                    submitCreateEpic={submitCreateEpic}
                                    closeCreateEpic={closeCreateEpic}
                                    epicTitleEditContainerRef={
                                        epicTitleEditContainerRef
                                    }
                                    epicTitleEditRef={epicTitleEditRef}
                                    epicEditSubmitting={epicEditSubmitting}
                                    setEpicEdit={setEpicEdit}
                                    submitEpicTitleEdit={submitEpicTitleEdit}
                                    openEpicTitleEdit={openEpicTitleEdit}
                                    epicInsertHover={epicInsertHover}
                                    openCreateEpicAt={openCreateEpicAt}
                                />

                                <div className={styles.sprintListScroll}>
                                    {orderedSprints.length === 0 &&
                                        !loading && (
                                            <div className={styles.emptyHint}>
                                                No sprints returned for this
                                                project.
                                            </div>
                                        )}

                                    {orderedSprints.map((sprint) => (
                                        <SprintSection
                                            key={sprint?.id}
                                            sprint={sprint}
                                            isBacklogSprint={isBacklogSprint}
                                            sprintMutationKey={
                                                sprintMutationKey
                                            }
                                            handleCompleteSprint={
                                                handleCompleteSprint
                                            }
                                            requestDeleteSprint={
                                                requestDeleteSprint
                                            }
                                            requestCompleteSprint={
                                                openCompleteSprintModal
                                            }
                                            handleStartSprint={
                                                handleStartSprint
                                            }
                                            sprintHasAtLeastOneTask={
                                                sprintHasAtLeastOneTask
                                            }
                                            hasAnyActiveSprint={
                                                hasAnyActiveSprint
                                            }
                                            openCreateSprintModal={
                                                openCreateSprintModal
                                            }
                                            openEditSprintModal={
                                                openEditSprintModal
                                            }
                                            sprintHScrollRefs={
                                                sprintHScrollRefs
                                            }
                                            syncHorizontalScroll={
                                                syncHorizontalScroll
                                            }
                                            epicsForBoard={epicsForBoard}
                                            taskBuckets={taskBuckets}
                                            epicGridStyle={epicGridStyle}
                                            EPIC_CREATE_DRAFT_ID={
                                                EPIC_CREATE_DRAFT_ID
                                            }
                                            onTaskClick={onTaskClick}
                                            projectId={projectId}
                                            dispatch={dispatch}
                                            filteredAssignees={
                                                filteredAssignees
                                            }
                                            noopSetLocalTaskList={
                                                noopSetLocalTaskList
                                            }
                                            refreshTasks={refreshTasks}
                                            createTaskStates={createTaskStates}
                                            initialCreateTask={
                                                initialCreateTask
                                            }
                                            setCreateTaskForLane={
                                                setCreateTaskForLane
                                            }
                                            openCreateTaskInLane={
                                                openCreateTaskInLane
                                            }
                                            createTaskFromSprintLane={
                                                createTaskFromSprintLane
                                            }
                                            isIssuesSprint={isIssuesSprint}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </DragDropContext>
            <CompleteSprintModal
                open={completeSprintModalState.open}
                sprintTitle={completeSprintModalState?.sprint?.title || ''}
                doneCount={completeSprintSummary.doneCount}
                incompleteCount={completeSprintSummary.incompleteCount}
                loading={
                    sprintSummaryStatus === 'loading' ||
                    !!completingCurrentSprint
                }
                moveToSprintId={completeSprintModalState.moveToSprintId}
                onMoveToChange={(value) =>
                    setCompleteSprintModalState((prev) => ({
                        ...prev,
                        moveToSprintId: value,
                    }))
                }
                targetOptions={completeModalOptions}
                newSprintSelected={isNewSprintSelected}
                newSprintTitle={newSprintTitlePreview}
                newSprintRange={completeSprintModalState.newSprintRange}
                onNewSprintRangeChange={(range) =>
                    setCompleteSprintModalState((prev) => ({
                        ...prev,
                        newSprintRange: range,
                    }))
                }
                onCancel={closeCompleteSprintModal}
                onConfirm={confirmCompleteSprint}
            />
            <CreateSprintModal
                open={sprintModalState.open}
                mode={sprintModalState.mode}
                initialSprint={sprintModalState.sprint}
                onCancel={closeSprintModal}
                projectId={projectId}
                onSaved={refreshTasks}
            />
        </div>
    )
}

export default DevelopmentSprintView
